/**
 * Entity list resource (CT-5277).
 *
 * Real signatures and snake_case bodies; only the transport is stubbed via
 * `mockTransport.js`. When an endpoint lands, change nothing here except the
 * path string if the backend team renames it.
 */

import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { request } from './mockTransport.js';

export { __resetMockEntitiesForTests } from './__mocks__/entities.mock.js';

/**
 * Map a raw API row onto the Title / Visual / Description card contract.
 *
 * The alias chains are the point. A tenant's Data Studio mapping decides which
 * source column lands in each key, and different resources have not settled on
 * one spelling — so the mapper accepts the handful of aliases in play and
 * flattens them. What it must never do is branch on entity type: that is the
 * single rule the whole domain exists to prove.
 *
 * A field the mapping did not fill comes back NULLISH, never `''`. Components
 * distinguish "absent" (omit the block entirely) from "empty" (render it
 * blank), and an empty string would collapse the two.
 */
export const mapApiEntityListItemToUi = (raw = {}) => ({
  id: raw.entity_id ?? raw.entityId ?? raw.id ?? '',
  title: raw.title ?? raw.name ?? raw.label ?? null,
  visual: raw.visual ?? raw.avatar_url ?? raw.avatarUrl ?? raw.image_url ?? raw.imageUrl ?? null,
  description: raw.description ?? raw.summary ?? null,
  badges: raw.badges ?? [],
  meta: raw.meta ?? [],
  // Exploratory (CT-5270 open question #11): Michael's Entity List Figma
  // carries a source system, a record code, an optional "Next Best Action"
  // callout and a type tag. None of these are in the ticket's stated card
  // contract (title/visual/description + badges/meta) — nullish everywhere
  // until product confirms they're in scope, so an unmapped tenant sees
  // nothing extra.
  code: raw.code ?? null,
  source: raw.source ?? null,
  nextBestAction: raw.next_best_action ?? raw.nextBestAction ?? null,
  typeTag: raw.type_tag ?? raw.typeTag ?? null,
  // Which type the row belongs to: the Records list shows every type together,
  // so a row has to say where its profile lives. Nullish for a payload that
  // does not send it — the list then falls back to the type it asked for.
  entityType: raw.entity_type ?? raw.entityType ?? null,
  // ISO timestamp of the last interaction. A row with none sorts as the oldest.
  lastInteraction: raw.last_interaction ?? raw.lastInteraction ?? null,
});

/**
 * List the records of one Unified Profile type.
 *
 * TODO(CT-5277 / backend): `POST /v1/workspace/profiles/entities/list` with
 * `{ entity_type, page, page_size, order_by, order_direction, filters, search }`.
 *
 * The response carries `filters` and `sorts` alongside the rows. They are part
 * of the answer, not a separate lookup: a Dealership and a Vehicle share no
 * fields, so what a type can be narrowed and ordered by is only knowable per
 * type — and it changes when a tenant changes its Data Studio mapping.
 */
// TODO(CT-5523 / backend): the All scope — `POST /v0/profile/entities/list` with no
// `entity_type`, merged across every type the caller may see — is assumed here.
export const listEntities = async ({
  entityType,
  page = 1,
  pageSize = 10,
  orderBy = 'title',
  orderDirection = 'asc',
  filters,
  search,
} = {}) => {
  const response = await request('/v0/profile/entities/list', {
    // Omitted for the All view: every type the caller may see, merged.
    ...(entityType ? { entity_type: entityType } : {}),
    page,
    page_size: pageSize,
    order_by: orderBy,
    order_direction: orderDirection,
    ...(filters ? { filters } : {}),
    ...(search ? { search } : {}),
  });
  return validateApiResponse(response, 'Failed to load entities');
};
